import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";
import { ComparisonTable } from "../shared/ComparisonTable";
import { COLORS, FONTS, SPRING } from "./constants";

export const Scene07Citations: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // --- Phase A: Bold Statement (0-150) ---
  const titleProgress = spring({
    frame: frame - 10,
    fps,
    config: SPRING.snappy,
  });
  const titleOpacity = interpolate(titleProgress, [0, 1], [0, 1]);
  const titleY = interpolate(titleProgress, [0, 1], [30, 0]);

  const subtitleOpacity = interpolate(frame, [30, 60], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // --- Phase B: Comparison Table (150-700) ---
  const tableOpacity = interpolate(frame, [150, 200], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Phase A text fades when table appears
  const phaseAFade = interpolate(frame, [140, 180], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // --- Phase C: Accuracy Stats (700-900) ---
  const tableFadeOut = interpolate(frame, [680, 720], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const statsFadeOut = interpolate(frame, [860, 900], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const stats = [
    { value: "85-93%", label: "Retrieval accuracy", color: COLORS.accentGreen },
    { value: "6", label: "Generation modes", color: COLORS.accentTeal },
    { value: "3-6", label: "Sources per answer", color: COLORS.accentBlue },
    { value: "H/M/L", label: "Confidence scoring", color: COLORS.accentPurple },
  ];

  return (
    <AbsoluteFill>
      <GridBackground />

      {/* Phase A: Bold Statement */}
      <AbsoluteFill style={{ opacity: phaseAFade }}>
        <div
          style={{
            position: "absolute",
            top: 200,
            left: 0,
            right: 0,
            textAlign: "center",
            fontFamily: FONTS.heading,
            fontSize: 60,
            fontWeight: 700,
            color: COLORS.textPrimary,
            opacity: titleOpacity,
            transform: `translateY(${titleY}px)`,
          }}
        >
          This isn't ChatGPT.
        </div>

        <div
          style={{
            position: "absolute",
            top: 280,
            left: 0,
            right: 0,
            textAlign: "center",
            fontFamily: FONTS.body,
            fontSize: 24,
            color: COLORS.accentTeal,
            opacity: subtitleOpacity,
          }}
        >
          Every answer is grounded in YOUR data.
        </div>
      </AbsoluteFill>

      {/* Phase B: Comparison Table */}
      <div
        style={{
          position: "absolute",
          top: 330,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          opacity: tableOpacity * tableFadeOut,
        }}
      >
        <ComparisonTable
          leftTitle="ChatGPT"
          rightTitle="EAKC"
          leftAccent="#EF4444"
          rightAccent="#34D399"
          delay={150}
          rows={[
            { feature: "Data source", left: "Internet (generic)", right: "Your company's docs" },
            { feature: "Citations", left: "\u274C None", right: "\u2705 Clickable sources" },
            { feature: "Privacy", left: "\u26A0\uFE0F May train on data", right: "\u2705 Never trains on yours" },
            { feature: "Freshness", left: "\uD83D\uDD50 Months-old", right: "\u26A1 Real-time synced" },
            { feature: "Multi-source", left: "\u274C Single chat", right: "\u2705 GitHub+Jira+Slack+Docs" },
            { feature: "Confidence", left: "\u274C Always confident", right: "\u2705 Shows confidence score" },
            { feature: "Contradictions", left: "\u274C Ignores conflicts", right: "\u2705 Flags them" },
          ]}
        />
      </div>

      {/* Phase C: Accuracy Stats */}
      {frame >= 700 && (
        <div
          style={{
            position: "absolute",
            top: 500,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            gap: 80,
            opacity: statsFadeOut,
          }}
        >
          {stats.map((stat, i) => {
            const delay = 710 + i * 10;
            const progress = spring({
              frame: frame - delay,
              fps,
              config: SPRING.snappy,
            });
            const opacity = interpolate(progress, [0, 1], [0, 1]);
            const y = interpolate(progress, [0, 1], [20, 0]);

            return (
              <div
                key={stat.label}
                style={{
                  textAlign: "center",
                  opacity,
                  transform: `translateY(${y}px)`,
                }}
              >
                <div
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 40,
                    fontWeight: 700,
                    color: stat.color,
                  }}
                >
                  {stat.value}
                </div>
                <div
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 14,
                    color: COLORS.textSecondary,
                    marginTop: 8,
                  }}
                >
                  {stat.label}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
